// Central facade for all reference data the calculator reads.
//
// The database is the source of truth: RetirementCalcViewModel loads these
// tables from GET /api/retirement-calculator/reference-data (adapted and
// validated by remoteReferenceData.ts) before calculating. The bundled copies
// below — extracted verbatim from the original
// SCERS_Retirement_Calculator_v6_0_7.html, see the individual files in this
// directory for provenance — are only the fallback for when that load fails.
//
// Every engine function that reads one of these tables takes a
// `RetirementReferenceData` object as an optional trailing parameter,
// defaulting to `defaultReferenceData` (this file).
import { tierConfig, MEMBERSHIP_TIER_OPTIONS } from './tierConfig';
import { erfTables } from './erfTables';
import { rapMortalityByDescription } from './mortalityTables';
import { rapOption1ByDescription } from './option1Tables';
import { rapServiceAssumptionPeriods } from './rapAssumptions';
import { NONSPOUSE_SURVIVOR_LIMIT_PCT } from './nonspouseSurvivorLimit';
import { SERVICE_CREDITS_PER_YEAR } from './constants';
import { BENEFICIARY_RELATIONSHIP_OPTIONS, BeneficiaryRelationshipOption, HELP_CONTENT, HelpContent } from './uiContent';

export type { BeneficiaryRelationshipOption, HelpContent } from './uiContent';

export interface TierCfg {
  name: string;
  minAge: number;
  erf: string;
  mort: string;
  formula: string;
  oldFormula?: boolean;
}

export type RapServiceAssumptionRow = readonly [
  tier: string, minAge: number, interest: number,
  memberMortality: string, memberSetback: number,
  beneficiaryMortality: string, beneficiarySetback: number,
  option1Description: string, erfDescription: string,
];
export type RapServiceAssumptionPeriod = readonly [effectiveFrom: string, effectiveTo: string, rows: readonly RapServiceAssumptionRow[]];
export type ErfRow = readonly [age: number, factor: number];
export type Option1HistoricalRow = readonly [age: number, tableValue: number, levelIncome62: string, levelIncome65: string];

export interface RetirementReferenceData {
  tierConfig: Record<string, TierCfg>;
  membershipTierOptions: readonly (readonly [code: string, label: string])[];
  erfTables: Record<string, readonly ErfRow[]>;
  rapMortalityByDescription: Record<string, readonly number[]>;
  rapOption1ByDescription: Record<string, readonly Option1HistoricalRow[]>;
  rapServiceAssumptionPeriods: readonly RapServiceAssumptionPeriod[];
  nonspouseSurvivorLimitPct: Record<number, number>;
  serviceCreditsPerYear: number;
  // UI copy only — never read by the engine.
  helpContent: Record<string, HelpContent>;
  beneficiaryRelationshipOptions: readonly BeneficiaryRelationshipOption[];
}

// The bundled fallback.
export const defaultReferenceData: RetirementReferenceData = {
  tierConfig: tierConfig as unknown as Record<string, TierCfg>,
  membershipTierOptions: MEMBERSHIP_TIER_OPTIONS,
  erfTables: erfTables as unknown as Record<string, readonly ErfRow[]>,
  rapMortalityByDescription: rapMortalityByDescription as unknown as Record<string, readonly number[]>,
  rapOption1ByDescription: rapOption1ByDescription as unknown as Record<string, readonly Option1HistoricalRow[]>,
  rapServiceAssumptionPeriods: rapServiceAssumptionPeriods as unknown as readonly RapServiceAssumptionPeriod[],
  nonspouseSurvivorLimitPct: NONSPOUSE_SURVIVOR_LIMIT_PCT as unknown as Record<number, number>,
  serviceCreditsPerYear: SERVICE_CREDITS_PER_YEAR,
  helpContent: HELP_CONTENT,
  beneficiaryRelationshipOptions: BENEFICIARY_RELATIONSHIP_OPTIONS,
};
